import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { toast } from '@kognitos/lattice';
import { useArchiveRuns } from '@/generated/hooks/jeeves/v1/api';
import type { V1Run } from '@/generated/hooks/jeeves/models';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { withRetry } from '@/shared/utils/errors';

/**
 * Archives a run. Reports progress and failure as toasts.
 *
 * Jeeves archives the run's pending exceptions with it, so the cached
 * exception queries are refreshed alongside the run's.
 *
 * @param automationId - The automation the runs belong to
 * @returns `archiveRun(run)`, resolving to whether the run was archived, and
 *   whether an archive is in flight
 */
export function useArchiveRun(automationId: string) {
  const { t } = useTranslation('automation');
  const queryClient = useQueryClient();
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);
  const archiveMutation = useArchiveRuns();

  const archiveRun = async (run: V1Run): Promise<boolean> => {
    const runName = run.name;
    if (!runName) return false;
    const toastId = toast.loading(t('automation.runs.archiveLoading'));
    try {
      await withRetry(() =>
        archiveMutation.mutateAsync({
          organization: organization || '',
          workspace: workspace || '',
          automation: automationId,
          data: { names: [runName] },
        }),
      );
      toast.dismiss(toastId);
      toast.success(t('automation.runs.archiveSuccess'));
      // Covers the runs list and the run itself, which other pages may have
      // cached, and the guidance center's exceptions, which would otherwise
      // wait for the 60s staleTime.
      const workspacePrefix =
        `/api/v1/organizations/${organization || ''}` +
        `/workspaces/${workspace || ''}`;
      const keyPrefixes = [
        `${workspacePrefix}/automations/${automationId}/runs`,
        `${workspacePrefix}/exceptions`,
      ];
      void queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey.some(
            part =>
              typeof part === 'string' &&
              keyPrefixes.some(prefix => part.startsWith(prefix)),
          ),
      });
      return true;
    } catch {
      toast.dismiss(toastId);
      toast.error(t('automation.runs.archiveError'));
      return false;
    }
  };

  return { archiveRun, isArchiving: archiveMutation.isPending };
}
